import {
  Download,
  Filter,
  Inbox,
  KanbanSquare,
  List,
  type LucideIcon,
  Plus,
  RefreshCw,
  Search,
  Settings as SettingsIcon,
  Sparkles,
  X,
  Zap,
} from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Command as CommandRoot,
} from '@/components/ui/Command';
import { KeyboardShortcutKey } from '@/components/ui/KeyboardShortcutKey';
import { Modal } from '@/components/ui/Modal';
import { ScoreBadge } from '@/components/ui/ScoreBadge';
import { Shortcut } from '@/components/ui/Shortcut';
import { cx } from '@/cva.config';
import { useJobs } from '@/hooks/useJobs';
import { useRetryFailed } from '@/hooks/useRetryFailed';
import { useToast } from '@/hooks/useToast';
import {
  useJobFilters,
  useSelectJob,
} from '@/providers/dashboard/JobBrowserContext';
import {
  useDisplayDensity,
  useOverlays,
  useShellNav,
} from '@/providers/dashboard/ShellContext';

type CommandGroupName =
  | 'Pipeline'
  | 'Navigate'
  | 'Filter'
  | 'Display'
  | 'Quick add'
  | 'Jobs';

interface CommandItemData {
  group: CommandGroupName;
  hint?: string;
  icon: LucideIcon;
  id: string;
  kbd?: string;
  label: string;
  score?: number | null;
}

export const CommandPaletteContent = () => {
  const jobs = useJobs();
  const { openAddUrl, closePalette } = useOverlays();
  const { setView } = useShellNav();
  const { setFilters } = useJobFilters();
  const select = useSelectJob();
  const { density, setDensity } = useDisplayDensity();
  const retryFailed = useRetryFailed();
  const toast = useToast();

  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleCommand = useCallback(
    (commandId: string) => {
      if (commandId.startsWith('job-')) {
        const id = Number.parseInt(commandId.slice(4), 10);

        if (!Number.isNaN(id)) {
          select(id);
          setView('feed');
        }

        return;
      }

      switch (commandId) {
        case 'run-scan':
          void fetch('/api/internal/scan', { method: 'POST' });
          toast.push('Scan started — scrape ▸ score ▸ tailor', 'info');
          break;
        case 'retry-failed':
          retryFailed.mutate();
          toast.push('Retrying failed jobs', 'info');
          break;
        case 'view-feed':
          setView('feed');
          break;
        case 'view-tracker':
          setView('tracker');
          break;
        case 'view-settings':
          setView('settings');
          break;
        case 'filter-ready':
          setFilters((f) => ({ ...f, status: 'ready' }));
          break;
        case 'filter-high':
          setFilters((f) => ({ ...f, minScore: 80 }));
          break;
        case 'filter-clear':
          setFilters({ minScore: 0, query: '', sources: [], status: 'all' });
          toast.push('Filters cleared', 'info');
          break;
        case 'toggle-density':
          setDensity(density === 'compact' ? 'comfortable' : 'compact');
          break;
        case 'add-url':
          openAddUrl();
          break;
        case 'scrape':
        case 'score':
        case 'tailor':
          toast.push('Coming soon', 'info');
          break;
        default:
          break;
      }
    },
    [
      retryFailed,
      toast,
      setView,
      select,
      setFilters,
      setDensity,
      density,
      openAddUrl,
    ],
  );

  const items: CommandItemData[] = useMemo(() => {
    const cmds: CommandItemData[] = [
      // Pipeline
      {
        group: 'Pipeline',
        hint: 'scrape → score → tailor',
        icon: RefreshCw,
        id: 'run-scan',
        kbd: '⌘R',
        label: 'Run full scan now',
      },
      {
        group: 'Pipeline',
        hint: 'all 6 sources',
        icon: Download,
        id: 'scrape',
        label: 'Scrape job boards',
      },
      {
        group: 'Pipeline',
        hint: 'claude haiku',
        icon: Zap,
        id: 'score',
        label: 'Score unscored jobs',
      },
      {
        group: 'Pipeline',
        hint: 'claude sonnet',
        icon: Sparkles,
        id: 'tailor',
        label: 'Tailor approved jobs',
      },
      {
        group: 'Pipeline',
        icon: RefreshCw,
        id: 'retry-failed',
        label: 'Retry failed scorings',
      },

      // Navigate
      {
        group: 'Navigate',
        icon: Inbox,
        id: 'view-feed',
        kbd: 'G F',
        label: 'Go to feed',
      },
      {
        group: 'Navigate',
        icon: KanbanSquare,
        id: 'view-tracker',
        kbd: 'G T',
        label: 'Go to tracker',
      },
      {
        group: 'Navigate',
        icon: SettingsIcon,
        id: 'view-settings',
        kbd: 'G S',
        label: 'Go to settings',
      },

      // Filters
      {
        group: 'Filter',
        icon: Filter,
        id: 'filter-ready',
        label: 'Show ready jobs',
      },
      {
        group: 'Filter',
        icon: Filter,
        id: 'filter-high',
        label: 'Show high matches (≥80)',
      },
      {
        group: 'Filter',
        icon: X,
        id: 'filter-clear',
        label: 'Clear all filters',
      },

      // Display
      {
        group: 'Display',
        icon: List,
        id: 'toggle-density',
        kbd: 'D',
        label: 'Toggle density (compact/comfortable)',
      },

      // Quick add
      {
        group: 'Quick add',
        icon: Plus,
        id: 'add-url',
        kbd: 'U',
        label: 'Add job by URL',
      },

      // Jobs (top 8 scored/ready)
      ...jobs
        .filter((j) => j.matchScore != null)
        .slice(0, 8)
        .map<CommandItemData>((j) => ({
          group: 'Jobs',
          hint: `score ${j.matchScore} · ${j.status}`,
          icon: Inbox,
          id: `job-${j.id}`,
          label: `${j.title} · ${j.company}`,
          score: j.matchScore,
        })),
    ];
    if (!query) return cmds;
    const formattedQuery = query.toLowerCase();
    return cmds.filter(
      (cmd) =>
        cmd.label.toLowerCase().includes(formattedQuery)
        || cmd.hint?.toLowerCase().includes(formattedQuery)
        || cmd.group.toLowerCase().includes(formattedQuery),
    );
  }, [query, jobs]);

  const grouped = useMemo(() => {
    const map = new Map<CommandGroupName, CommandItemData[]>();
    for (const item of items) {
      const existing = map.get(item.group);
      if (existing) {
        existing.push(item);
      } else {
        map.set(item.group, [item]);
      }
    }
    return map;
  }, [items]);

  return (
    <Modal
      align="top"
      initialFocus={inputRef}
      onClose={closePalette}
      open
      size="xl"
      zIndex="palette"
    >
      <CommandRoot loop shouldFilter={false}>
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <Search className="size-4 text-foreground-muted" />
          <CommandInput
            onValueChange={setQuery}
            placeholder="Type a command or search…"
            ref={inputRef}
            value={query}
          />
          <KeyboardShortcutKey>esc</KeyboardShortcutKey>
        </div>

        <CommandList>
          <CommandEmpty>No commands match &ldquo;{query}&rdquo;</CommandEmpty>
          {Array.from(grouped.entries()).map(([group, gItems]) => (
            <CommandGroup heading={group} key={group}>
              {gItems.map((item) => {
                const IconComponent = item.icon;
                return (
                  <CommandItem
                    key={item.id}
                    onSelect={() => {
                      handleCommand(item.id);
                      closePalette();
                    }}
                    value={`${item.group} ${item.label} ${item.hint ?? ''}`}
                  >
                    {item.score != null ? (
                      <ScoreBadge score={item.score} size="sm" />
                    ) : (
                      <span
                        className={cx(
                          'flex size-7 shrink-0 items-center justify-center rounded-md border border-border text-foreground-muted group-data-[selected=true]:border-accent group-data-[selected=true]:text-accent',
                        )}
                      >
                        <IconComponent className="size-3.5" />
                      </span>
                    )}
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.hint && (
                      <span className="hidden max-w-45 truncate font-mono text-2xs lg:text-xs text-subtle sm:inline">
                        {item.hint}
                      </span>
                    )}
                    {item.kbd && <Shortcut keys={item.kbd} />}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          ))}
        </CommandList>

        <div className="flex items-center justify-between border-t border-border bg-surface-sunken/40 px-4 py-2.5">
          <div className="flex items-center gap-3 text-2xs lg:text-xs text-foreground-muted">
            <div className="flex items-center gap-1">
              <KeyboardShortcutKey>↑</KeyboardShortcutKey>
              <KeyboardShortcutKey>↓</KeyboardShortcutKey> navigate
            </div>
            <div className="flex items-center gap-1">
              <KeyboardShortcutKey>↵</KeyboardShortcutKey> select
            </div>
          </div>
          <div className="font-mono text-2xs lg:text-xs text-subtle tabular-nums">
            {items.length} commands
          </div>
        </div>
      </CommandRoot>
    </Modal>
  );
};
